/*
  Opens the Shopify chat widget from an in-page link.

  Any link pointing at "#chat" (e.g. the contact page "Chat with us" button)
  opens the widget instead of navigating. The href stays a real page URL, so
  the link still works if the widget is unavailable or blocked.

  Two widget generations are supported because a store can be on either:
    - `<shopify-chat>` (current): a custom element with a public `show()`
      method, so no shadow-DOM poking is needed.
    - `<inbox-online-store-chat>` (legacy Shopify Inbox): exposes no API, so
      the only handle is its own toggle button inside an open shadow root.
*/

(function () {
  const CHAT_LINK_SELECTOR = 'a[href*="#chat"]';
  const CHAT_ELEMENT_SELECTOR = 'shopify-chat';
  const LEGACY_WIDGET_SELECTOR = 'inbox-online-store-chat';
  const LEGACY_TOGGLE_SELECTOR = '[data-spec="toggle-button"]';
  // Both widgets are injected asynchronously; give them a bounded window.
  const WAIT_TIMEOUT_MS = 5000;
  const POLL_INTERVAL_MS = 100;

  function findChatElement() {
    const el = document.querySelector(CHAT_ELEMENT_SELECTOR);
    return el && typeof el.show === 'function' ? el : null;
  }

  function findLegacyToggle() {
    // Light DOM first, in case a version stops using a shadow root.
    const direct = document.querySelector(LEGACY_TOGGLE_SELECTOR);
    if (direct) return direct;

    const widget = document.querySelector(LEGACY_WIDGET_SELECTOR);
    return widget && widget.shadowRoot
      ? widget.shadowRoot.querySelector(LEGACY_TOGGLE_SELECTOR)
      : null;
  }

  // Resolves with an opener function, or null once the widget is ruled out.
  function findOpener() {
    const chat = findChatElement();
    if (chat) {
      return function () {
        if (!chat.open) chat.show();
      };
    }

    const toggle = findLegacyToggle();
    if (toggle) {
      return function () {
        if (toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
      };
    }

    return null;
  }

  function waitForOpener() {
    return new Promise(function (resolve) {
      const existing = findOpener();
      if (existing) {
        resolve(existing);
        return;
      }

      const deadline = Date.now() + WAIT_TIMEOUT_MS;
      const timer = setInterval(function () {
        const opener = findOpener();
        if (opener || Date.now() > deadline) {
          clearInterval(timer);
          resolve(opener);
        }
      }, POLL_INTERVAL_MS);
    });
  }

  document.addEventListener('click', function (event) {
    const link = event.target.closest(CHAT_LINK_SELECTOR);
    if (!link) return;

    // The custom element is upgraded before its internals render, so its mere
    // presence is the signal that a chat widget exists on this page at all.
    const widgetPresent =
      document.querySelector(CHAT_ELEMENT_SELECTOR) ||
      document.querySelector(LEGACY_WIDGET_SELECTOR) ||
      findLegacyToggle();
    if (!widgetPresent) return;

    event.preventDefault();

    waitForOpener().then(function (opener) {
      if (opener) {
        opener();
      } else {
        window.location.href = link.href;
      }
    });
  });
})();
